/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from "react";
import type { SlideEntry } from "./types";

interface SlideContextValue {
  index: number;
  total: number;
  slideHash: (id: string) => string;
}

const SlideContext = createContext<SlideContextValue | null>(null);

export function SlideContextProvider({
  index,
  total,
  slides,
  children,
}: {
  index: number;
  total: number;
  slides: SlideEntry[];
  children: ReactNode;
}) {
  const slideHash = (id: string): string => {
    const i = slides.findIndex((s) => s.id === id);
    return i >= 0 ? `#slide-${i + 1}` : "";
  };

  return (
    <SlideContext.Provider value={{ index, total, slideHash }}>
      {children}
    </SlideContext.Provider>
  );
}

export function useSlideContext(): SlideContextValue | null {
  return useContext(SlideContext);
}

/**
 * A clickable superscript reference. Renders `<sup>` with an inner `<a>`
 * whose href points to the target slide (Sources slide for numerical refs,
 * appendix slides for letter refs).
 *
 * Usage:
 *   <SupLink to="sources">1</SupLink>
 *   <SupLink to="ev-target">G</SupLink>
 *
 * Inherits surrounding text color by default; hover/focus turns blue +
 * underline for affordance. Pass `className` to override.
 */
export function SupLink({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  const ctx = useSlideContext();
  const href = ctx?.slideHash(to) ?? "";
  return (
    <sup className={className}>
      <a
        href={href}
        className="text-inherit transition-colors hover:text-blue-700 hover:underline focus:text-blue-700 focus:underline"
      >
        {children}
      </a>
    </sup>
  );
}
